import React, { type FC } from 'react'
import { Box, CircularProgress, DialogContent, type SxProps, type Theme, Typography } from '@mui/material'

import { ConnectwareError, Translation } from '../../../../domain'

import { FormattedTranslation } from '../../Internationalization'
import { Form, type FormData } from './Form'
import {
    useConfigurationTitle,
    useFileContent,
    useHasNumberedSteps,
    useHasParameters,
    useId,
    useIsEditable,
    useIsIdEditable,
    useNoConfigurationText,
    useParameters,
    useSchema,
    useUpdateServiceParameters,
} from './Hooks'

const SchemaForm: FC = () => {
    const updateParameters = useUpdateServiceParameters()

    const noConfigurationText = useNoConfigurationText()
    const isEditable = useIsEditable()
    const isIdEditable = useIsIdEditable()
    const file = useFileContent()
    const id = useId()
    const parameters = useParameters()
    const schema = useSchema()

    if (file === null) {
        /**
         * File has not been loaded, render nothing
         */
        return null
    }

    if (schema === null) {
        /**
         * File has been loaded but schema hasnt, show loader
         */
        return <CircularProgress />
    }

    if (ConnectwareError.is(schema) || !parameters) {
        /**
         * Errors get displayed elsewhere, no need to display anything here
         */
        return null
    }

    return (
        <Form<FormData>
            id="serviceConfigurationForm"
            schema={schema}
            formData={isIdEditable && typeof id === 'string' ? { id, ...parameters } : parameters}
            readonly={!isEditable}
            onChange={({ id, ...parameters }) =>
                updateParameters(
                    /**
                     * If the id is not present, then it is not relevant
                     * Thus a null value should be passed instead
                     */
                    id ?? null,
                    parameters
                )
            }
            emptyForm={
                <Typography data-testid="nothing-to-configure-message" variant="body2" align="left">
                    <FormattedTranslation id={noConfigurationText} />
                </Typography>
            }
        />
    )
}

const opaqueStyle: SxProps<Theme> = { opacity: 0.5 }
const formWrapperStyle: SxProps<Theme> = { maxHeight: 'calc(100vh - 250px)', overflowY: 'inherit', p: 2 }

export const ConfigurationSection: FC = () => {
    const configurationTitle = useConfigurationTitle()
    const hasParameters = useHasParameters()
    const hasSteps = useHasNumberedSteps()

    return (
        <DialogContent>
            <Typography variant="body1" align="left" sx={hasParameters ? {} : opaqueStyle}>
                {hasSteps && '2. '}
                <FormattedTranslation id={configurationTitle} />
            </Typography>
            {!hasParameters && (
                <Typography variant="body2" align="left" sx={opaqueStyle}>
                    <FormattedTranslation id={Translation.SERVICE_CONFIGURE_HINT} />
                </Typography>
            )}
            <Box sx={formWrapperStyle}>
                <SchemaForm />
            </Box>
        </DialogContent>
    )
}
